/**
 * 매장 저장용량 탭 — 사진첩(사진·동영상)·알림장/공지 첨부·계약서·기타 원본 집계 + 실 S3 물리 용량(온디맨드).
 *  현재는 용량 제한 없음(무제한): 사용량·구성만 표시.
 */
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Video,
  Image as ImageIcon,
  MessageSquare,
  Megaphone,
  FileText,
  Files,
  Infinity as InfinityIcon,
  HardDrive,
  type LucideIcon,
} from 'lucide-react';
import { companyApi } from '@/services/api';
import type { CompanyStorageDto } from '@/services/api/members';

function fmtBytes(n: number): string {
  if (!n || n <= 0) return '0 B';
  const pb = n / 1024 ** 5;
  if (pb >= 1) return `${pb.toFixed(2)} PB`;
  const tb = n / 1024 ** 4;
  if (tb >= 1) return `${tb.toFixed(2)} TB`;
  const gb = n / 1024 ** 3;
  if (gb >= 1) return `${gb.toFixed(2)} GB`;
  const mb = n / 1024 ** 2;
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  const kb = n / 1024;
  if (kb >= 1) return `${kb.toFixed(0)} KB`;
  return `${n} B`;
}

const CAT_STYLE: Record<
  string,
  { color: string; bar: string; icon: LucideIcon }
> = {
  video: { color: 'text-blue-700', bar: '#378ADD', icon: Video },
  photo: { color: 'text-emerald-700', bar: '#1D9E75', icon: ImageIcon },
  notice: { color: 'text-violet-700', bar: '#7F77DD', icon: MessageSquare },
  announcement: { color: 'text-indigo-700', bar: '#534AB7', icon: Megaphone },
  contract: { color: 'text-orange-700', bar: '#D85A30', icon: FileText },
  contract_render: { color: 'text-orange-600', bar: '#F0997B', icon: FileText },
  thumbnail: { color: 'text-violet-600', bar: '#AFA9EC', icon: ImageIcon },
  etc: { color: 'text-gray-600', bar: '#888780', icon: Files },
};

// 카드/구성바에는 안 넣고 내역 테이블에만 들여쓰기로 표시하는 파생 카테고리
const SUB_KEYS = ['contract_render', 'thumbnail'];

export function CompanyStoragePanel({ companySeq }: { companySeq: number }) {
  const [wantPhysical, setWantPhysical] = useState(false);
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery<CompanyStorageDto>({
    queryKey: ['companyStorage', companySeq, wantPhysical],
    queryFn: () => companyApi.getCompanyStorage(companySeq, wantPhysical),
    staleTime: 30 * 1000,
  });

  // 보존기간 편집 (빈값=전역 기본 사용)
  const [retentionInput, setRetentionInput] = useState<string>('');
  useEffect(() => {
    setRetentionInput(data?.retentionDays != null ? String(data.retentionDays) : '');
  }, [data?.retentionDays]);
  const saveRetentionMutation = useMutation({
    mutationFn: (days: number | null) => companyApi.updateCompanyInfo(companySeq, { retentionDays: days }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['companyStorage', companySeq] });
      qc.invalidateQueries({ queryKey: ['companyDetail', String(companySeq)] });
    },
  });

  if (isLoading) {
    return <div className="rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-400">저장용량 불러오는 중…</div>;
  }
  if (error || !data) {
    return <div className="rounded-lg border border-gray-200 bg-white p-6 text-sm text-red-500">저장용량을 불러오지 못했습니다.</div>;
  }

  const total = data.totalBytes;
  const cats = data.categories ?? [];
  const physical = data.physical;

  // 내역 테이블 행 — 원본 카테고리 순서 + 계약서 렌더/썸네일(파생)을 들여쓰기로 삽입
  const catMap = new Map(cats.map((c) => [c.key, c] as const));
  const tableRows: typeof cats = [];
  for (const key of ['video', 'photo', 'notice', 'announcement', 'contract', 'contract_render']) {
    const c = catMap.get(key);
    if (c) tableRows.push(c);
  }
  if ((physical?.computed || physical?.estimated) && physical.thumbnailCount > 0) {
    tableRows.push({
      key: 'thumbnail',
      label: physical.estimated ? '썸네일 (사진·영상, 추정)' : '썸네일 (사진·영상)',
      bytes: physical.thumbnailBytes,
      count: physical.thumbnailCount,
      recentAt: null,
    });
  }
  const etcCat = catMap.get('etc');
  if (etcCat) tableRows.push(etcCat);

  return (
    <div className="space-y-4">
      {/* 상단 요약 — 실 S3 / 원본 집계 */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="rounded-xl bg-gray-50 p-4">
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <HardDrive size={13} /> 실 S3 용량 <span className="text-gray-400">(썸네일·렌더 포함)</span>
          </div>
          {physical?.computed ? (
            <>
              <div className="mt-1 text-2xl font-bold text-gray-900">{fmtBytes(physical.bytes)}</div>
              <div className="mt-0.5 text-[11px] text-gray-500">실제 S3 객체 기준 · {physical.objectCount.toLocaleString()}개</div>
            </>
          ) : physical?.estimated ? (
            <>
              <div className="mt-1 flex items-center gap-1.5">
                <span className="text-2xl font-bold text-gray-900">≈ {fmtBytes(physical.bytes)}</span>
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700">추정치</span>
              </div>
              <div className="mt-0.5 text-[11px] text-gray-500">
                파일 과다({physical.objectCount.toLocaleString()}개)로 원본은 DB 크기, 썸네일은 표본 {physical.sampleSize}개 평균으로 추정
              </div>
            </>
          ) : physical?.tooMany ? (
            <>
              <div className="mt-1 text-lg font-semibold text-gray-500">파일 과다</div>
              <div className="mt-0.5 text-[11px] text-gray-500">파일이 너무 많아 실측 생략 · 아래 원본 집계 참고</div>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setWantPhysical(true)}
              className="mt-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:border-primary-500 hover:text-primary-600"
            >
              실 S3 용량 계산
            </button>
          )}
        </div>
        <div className="rounded-xl bg-gray-50 p-4">
          <div className="text-xs text-gray-500">원본 집계 <span className="text-gray-400">(용도별 합)</span></div>
          <div className="mt-1 text-2xl font-bold text-gray-900">{fmtBytes(total)}</div>
          <div className="mt-0.5 flex items-center gap-2 text-[11px] text-gray-500">
            <span>원본 파일 · {data.totalCount.toLocaleString()}건</span>
            <span className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2 py-0.5 text-gray-500">
              <InfinityIcon size={11} /> 제한 없음
            </span>
          </div>
        </div>
      </div>

      {/* 보존기간 설정 — 휘발성(사진첩·알림장 첨부) 미디어 삭제 기준 */}
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3">
        <div className="min-w-0">
          <div className="text-sm font-medium text-gray-800">미디어 보존기간</div>
          <p className="mt-0.5 text-[11px] text-gray-500">
            사진첩·알림장 첨부는 이 기간이 지나면 자동 정리됩니다. 펫 등록사진·계약서 등은 대상 아님.
            현재 적용 <span className="font-medium text-gray-700">{data.effectiveRetentionDays}일</span>
            {data.retentionDays == null && <span className="text-gray-400"> (전역 기본 {data.defaultRetentionDays}일)</span>}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <input
            type="number"
            min={0}
            value={retentionInput}
            onChange={(e) => setRetentionInput(e.target.value)}
            placeholder={`기본 ${data.defaultRetentionDays}`}
            className="w-24 rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
          <span className="text-xs text-gray-500">일</span>
          <button
            type="button"
            disabled={saveRetentionMutation.isPending}
            onClick={() => {
              const trimmed = retentionInput.trim();
              const days = trimmed === '' ? null : Number(trimmed);
              saveRetentionMutation.mutate(Number.isFinite(days as number) ? days : null);
            }}
            className="rounded-md border border-gray-300 bg-white px-3 py-1 text-xs font-medium text-gray-700 hover:border-primary-500 hover:text-primary-600 disabled:opacity-50"
          >
            {saveRetentionMutation.isPending ? '저장 중…' : '저장'}
          </button>
        </div>
      </div>

      {/* 용도별 구성 바 */}
      {total > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="mb-2 text-xs font-medium text-gray-700">용도별 구성 (원본)</div>
          <div className="flex h-2.5 overflow-hidden rounded-full bg-gray-100">
            {cats.map((c) => {
              const pct = total > 0 ? (c.bytes / total) * 100 : 0;
              if (pct <= 0) return null;
              return <div key={c.key} style={{ width: `${pct}%`, background: CAT_STYLE[c.key]?.bar ?? '#888780' }} title={c.label} />;
            })}
          </div>
          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-600">
            {cats.map((c) => (
              <span key={c.key} className="inline-flex items-center gap-1">
                <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: CAT_STYLE[c.key]?.bar ?? '#888780' }} />
                {c.label} {total > 0 ? Math.round((c.bytes / total) * 100) : 0}%
              </span>
            ))}
          </div>
        </div>
      )}

      {/* 카테고리 카드 (파생 제외) */}
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
        {cats.filter((c) => !SUB_KEYS.includes(c.key)).map((c) => {
          const st = CAT_STYLE[c.key] ?? CAT_STYLE.etc;
          const Icon = st.icon;
          return (
            <div key={c.key} className="rounded-xl border border-gray-200 bg-white p-3">
              <div className={`flex items-center gap-1.5 text-xs font-medium ${st.color}`}>
                <Icon size={14} /> {c.label}
              </div>
              <div className="mt-1 text-lg font-bold text-gray-900">{fmtBytes(c.bytes)}</div>
              <div className="text-[11px] text-gray-500">{c.count.toLocaleString()}건</div>
            </div>
          );
        })}
      </div>

      {/* 내역 테이블 */}
      <div>
        <div className="mb-1.5 text-xs font-medium text-gray-700">저장 내역</div>
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <div className="grid grid-cols-[1.6fr_0.7fr_1fr_0.9fr] bg-gray-50 text-[11px] text-gray-500">
            <span className="px-3 py-2">유형</span>
            <span className="px-3 py-2 text-right">파일 수</span>
            <span className="px-3 py-2 text-right">용량</span>
            <span className="px-3 py-2">최근</span>
          </div>
          {cats.length === 0 && (
            <div className="px-3 py-6 text-center text-xs text-gray-400">저장된 파일이 없습니다.</div>
          )}
          {tableRows.map((c) => {
            const sub = SUB_KEYS.includes(c.key);
            return (
              <div key={c.key} className="grid grid-cols-[1.6fr_0.7fr_1fr_0.9fr] border-t border-gray-100 text-[13px]">
                <span className={`px-3 py-2.5 ${sub ? 'pl-6 text-gray-500' : 'text-gray-800'}`}>
                  {sub && <span className="mr-1 text-gray-400">└</span>}
                  {c.label}
                </span>
                <span className="px-3 py-2.5 text-right tabular-nums">{c.count.toLocaleString()}</span>
                <span className="px-3 py-2.5 text-right font-medium tabular-nums">{fmtBytes(c.bytes)}</span>
                <span className="px-3 py-2.5 text-gray-500">{c.recentAt ?? '-'}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-lg bg-gray-50 px-3 py-2.5 text-[12px] text-gray-500">
        <HardDrive size={14} className="mt-0.5 shrink-0" />
        <span>
          현재 용량 제한 없음(무제한). 원본 집계는 DB(FILE_SIZE) 기준 즉시 계산. 실 S3 용량은 원본+썸네일 HeadObject 합산(온디맨드).
          알림장 ‘직접첨부’(사진첩 미저장) 파일은 알림장 첨부에 포함되며 사진첩과 중복 없이 1회만 집계됩니다.
        </span>
      </div>
    </div>
  );
}
